/**
 * Analytics Route Tracker Component
 * Automatically tracks screen_view and screen_time on route changes
 * @module components/AnalyticsRouteTracker
 */

import type React from 'react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackScreenTime, trackScreenView } from '../../services/analytics.service';

/**
 * Maps React Router URL paths to standard screen names matching Mobile naming conventions
 */
export const getStandardScreenName = (pathname: string): string => {
  if (pathname === '/') return 'Home';
  if (pathname.startsWith('/chat/')) return 'Conversation';
  if (pathname.startsWith('/companion/')) return 'SahabeProfile';
  if (pathname === '/subscription') return 'Subscription';
  if (pathname === '/settings') return 'Settings_Usage';
  if (pathname === '/settings/delete-account') return 'DeleteAccount';
  if (pathname === '/sponsor') return 'SponsorList';
  if (pathname.startsWith('/sponsor/')) return 'SponsorDetail';
  if (pathname === '/login') return 'Login';
  if (pathname === '/register') return 'Register';
  if (pathname === '/forgot-password') return 'ForgotPassword';
  if (pathname === '/auth/callback') return 'Login_OAuthCallback';
  if (pathname === '/about') return 'AboutUs';
  if (pathname === '/faq') return 'FAQ';
  if (pathname === '/contact') return 'Contact';
  if (pathname === '/terms') return 'TermsOfService';
  if (pathname === '/privacy') return 'PrivacyPolicy';
  if (pathname === '/disclaimer') return 'Disclaimer';

  // Fallback: clean pathname
  const cleaned = pathname.replace(/^\//, '').replace(/\//g, '_');
  return cleaned || 'Home';
};

export const AnalyticsRouteTracker: React.FC = () => {
  const location = useLocation();
  const currentScreenRef = useRef<string>(getStandardScreenName(location.pathname));
  const startTimeRef = useRef<number>(Date.now());
  const isFirstRender = useRef<boolean>(true);

  useEffect(() => {
    const nextScreen = getStandardScreenName(location.pathname);
    const prevScreen = currentScreenRef.current;
    const now = Date.now();
    const elapsedSeconds = (now - startTimeRef.current) / 1000;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      document.title = `TheyChat | ${nextScreen}`;
      trackScreenView(nextScreen);
      startTimeRef.current = now;
      currentScreenRef.current = nextScreen;
      return;
    }

    // Do nothing if the screen hasn't changed (e.g. query params change or same screen)
    if (prevScreen === nextScreen) {
      return;
    }

    // Log screen_time for previous screen
    if (prevScreen && elapsedSeconds >= 0.5) {
      trackScreenTime({
        screenName: prevScreen,
        durationSeconds: elapsedSeconds,
        nextScreen: nextScreen,
      });
    }

    // Update document title and log screen_view for new screen
    document.title = `TheyChat | ${nextScreen}`;
    trackScreenView(nextScreen);

    // Reset timer
    startTimeRef.current = now;
    currentScreenRef.current = nextScreen;
  }, [location.pathname]);

  // Track screen_time when user closes tab/window or unmounts
  useEffect(() => {
    const handleBeforeUnload = () => {
      const elapsedSeconds = (Date.now() - startTimeRef.current) / 1000;
      if (currentScreenRef.current && elapsedSeconds >= 0.5) {
        trackScreenTime({
          screenName: currentScreenRef.current,
          durationSeconds: elapsedSeconds,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload();
    };
  }, []);

  return null;
};

export default AnalyticsRouteTracker;
